'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  createCheckoutSession,
  createTopupCheckoutSession,
  getSubscription,
} from '@/lib/stripe-client';
import {
  CREDIT_TOPUP_CREDITS_PER_UNIT,
  CREDIT_TOPUP_MIN_QUANTITY,
} from '@/config/stripe-plans.config';
import { loggedToast } from '@/lib/logged-toast';
import { Slider, SliderThumb } from '@/components/ui/slider';
import { LocaleLink } from '@/components/locale-link';
import type { StripeProduct } from '@/lib/stripe-products';
import { useSession } from '@/hooks/use-session';
import { ContactSalesDialog } from './contact-sales-dialog';

type PlanKey = 'basic' | 'topup' | 'pro' | 'enterprise';
type BillingPeriod = 'monthly' | 'yearly';

/**
 * Top-up slider bounds, in euros (one euro = one Stripe unit = 100 credits).
 *
 * The server accepts up to `CREDIT_TOPUP_MAX_QUANTITY` (500); the slider
 * stops at 100 because that is the range people actually buy, and a scale
 * that runs to 500 makes every realistic amount a thumb-width apart. Larger
 * purchases are still possible — the quantity stays adjustable on Stripe's
 * own page. The floor is the server's minimum top-up (5 €).
 */
const TOPUP_SLIDER_MIN = CREDIT_TOPUP_MIN_QUANTITY;
const TOPUP_SLIDER_MAX = 100;
const TOPUP_SLIDER_DEFAULT = 10;

interface Feature {
  text: string;
  negative?: boolean;
}

interface PlanCard {
  key: PlanKey;
  title: string;
  description: string;
  features: Feature[];
  price?: {
    amount: string;
    period: string;
    subline?: string;
    vatNote?: string;
  };
  free?: boolean;
  topup?: boolean;
  contact?: boolean;
  stripePriceId?: string;
  highlighted?: boolean;
}

interface PlansProps {
  stripeProducts: StripeProduct[];
}

// Display prices (net) per billing period. Yearly shows the per-month
// equivalent (annual / 12) so the card compares apples to apples; the
// "billed yearly" subline keeps the cadence clear. Both values are NET — the
// "+ 19% VAT" hint sits underneath, and Stripe Checkout adds the tax on top.
const PRO_PRICE_BY_PERIOD: Record<BillingPeriod, string> = {
  monthly: '40 €',
  yearly: '30 €',
};

// Per-call credit costs, mirroring the consolidated table in the v1 API
// reference (backend/server/v1/openapi.py). `credits` is the default cost;
// `realtime` is the cost when forcing a live fetch (`fetch_realtime=true`).
// `mcp` is the equivalent MCP tool where one exists. `earlierVersion` is the
// flat cost of downloading an earlier version of a register document
// (`document_id` from the documents list) — always fetched on demand.
interface CreditRow {
  key: string;
  rest: string;
  mcp: string | null;
  credits: number;
  realtime?: number;
  earlierVersion?: number;
}

const CREDIT_ROWS: CreditRow[] = [
  { key: 'autocomplete', rest: 'GET /companies/autocomplete', mcp: 'autocomplete_companies', credits: 0, realtime: 5 },
  { key: 'search', rest: 'GET /companies/search', mcp: 'search_companies', credits: 1, realtime: 5 },
  { key: 'detail', rest: 'GET /companies/{id}', mcp: 'get_company', credits: 1, realtime: 10 },
  { key: 'history', rest: 'GET /companies/{id}/history', mcp: 'get_company_history', credits: 1, realtime: 10 },
  { key: 'financials', rest: 'GET /companies/{id}/financials', mcp: 'get_company_financials', credits: 5 },
  { key: 'shareholders', rest: 'GET /companies/{id}/shareholders', mcp: 'get_company_shareholders', credits: 10, realtime: 20 },
  { key: 'ubo', rest: 'GET /companies/{id}/ubo', mcp: 'get_company_ubo', credits: 20, realtime: 50 },
  { key: 'documentsList', rest: 'GET /companies/{id}/documents', mcp: null, credits: 5 },
  { key: 'documents', rest: 'GET /companies/{id}/documents/download', mcp: null, credits: 10, realtime: 20, earlierVersion: 10 },
  { key: 'documentsWeb', rest: '—', mcp: null, credits: 10 },
];

// Cost of POST /subscriptions = data endpoint's realtime base + mode surcharge
// (+10 one-time, +50 recurring). Mirrors subscription_credits in the backend.
interface SubscriptionRow {
  key: string;
  oneTime: number;
  recurring: number;
}

const SUBSCRIPTION_ROWS: SubscriptionRow[] = [
  { key: 'detail', oneTime: 20, recurring: 60 },
  { key: 'history', oneTime: 20, recurring: 60 },
  { key: 'shareholders', oneTime: 30, recurring: 70 },
  { key: 'ubo', oneTime: 60, recurring: 100 },
  { key: 'documents', oneTime: 30, recurring: 70 },
];

const Plans = ({ stripeProducts }: PlansProps) => {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  // Any logged-in user can upgrade. Anonymous visitors see the "Coming soon"
  // CTA — they need to sign up before checkout, and the create-checkout-session
  // route would 401 them anyway.
  const upgradeEnabled = !!session?.user?.sub;
  const [contactOpen, setContactOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('yearly');
  // Signup-trial state. A trialing user holds the Pro role (so the Pro card
  // is their "current plan"), but has no payment method — the card must
  // still offer checkout, since that IS the conversion path.
  const [isTrialing, setIsTrialing] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  // Tracks which price's checkout session is in flight. The button stays in
  // its loading state until either the redirect to Stripe kicks in (success)
  // or the request errors out, whichever comes first.
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  // Euros of credit the slider is on. One euro is one Stripe line-item unit
  // (100 credits); the amount is still adjustable on the Stripe page, so this
  // is a starting point rather than a commitment.
  const [topupEuros, setTopupEuros] = useState(TOPUP_SLIDER_DEFAULT);

  useEffect(() => {
    if (!session?.user?.sub) {
      setIsTrialing(false);
      setTrialEnd(null);
      return;
    }
    let cancelled = false;
    getSubscription()
      .then((data) => {
        if (cancelled) return;
        const subscription = data?.subscription;
        setIsTrialing(subscription?.status === 'TRIALING');
        setTrialEnd(subscription?.trialEnd ?? null);
      })
      .catch(() => {
        // Best-effort: without the flag the card just shows "Current plan".
      });
    return () => {
      cancelled = true;
    };
  }, [session?.user?.sub]);

  const handleUpgrade = async (priceId: string) => {
    setPendingPriceId(priceId);
    try {
      await createCheckoutSession(priceId);
      // On success, createCheckoutSession redirects via window.location.href.
      // Leave the spinner up — clearing here would flash the button back to its
      // normal state during the (sub-second) navigation. The page unload is
      // imminent, no state will be observed.
    } catch {
      loggedToast.error(t('pages.plans.checkoutError'));
      setPendingPriceId(null);
    }
  };

  // Sentinel used in `pendingPriceId` and the `?upgrade=` param for the
  // credit top-up checkout, which has no Stripe price id of its own on the
  // client (the server resolves it by lookup key). Stripe price ids all
  // start with "price_", so the sentinel can't collide.
  const TOPUP_KEY = 'topup';

  const handleTopup = async () => {
    setPendingPriceId(TOPUP_KEY);
    try {
      await createTopupCheckoutSession(topupEuros);
      // Redirecting — keep the spinner, same as handleUpgrade.
    } catch {
      loggedToast.error(t('pages.plans.checkoutError'));
      setPendingPriceId(null);
    }
  };

  // Returning from Stripe Checkout via the browser Back button restores this
  // page from the bfcache with `pendingPriceId` still set, leaving the button
  // stuck in its loading state. `pageshow` with `persisted` fires exactly on
  // that restore — clear the flag so the button is clickable again.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPendingPriceId(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Guests can't check out yet — send them to Auth0 signup, then bring them
  // straight back to the plan they picked. The `upgrade` query param triggers
  // checkout automatically once the session is live (see the effect below), so
  // signing up feels like a single uninterrupted "upgrade" flow.
  const handleSignupToUpgrade = (priceId?: string) => {
    const base = window.location.pathname;
    const returnTo = priceId
      ? `${base}?upgrade=${encodeURIComponent(priceId)}`
      : base;
    try {
      navigator.sendBeacon(
        '/api/auth/signup-click',
        new Blob([JSON.stringify({ source: 'plans_upgrade', pathname: base })], {
          type: 'application/json',
        }),
      );
    } catch {
      // Tracking must never block navigation.
    }
    window.location.href = `/auth/login?screen_hint=signup&returnTo=${encodeURIComponent(returnTo)}`;
  };

  // After a guest signs up and returns with `?upgrade=<priceId>`, resume their
  // checkout automatically. Fires once the session is live; the ref guards
  // against a re-trigger if the effect re-runs, and we strip the param from the
  // URL so a refresh or back-navigation doesn't kick off checkout again.
  const autoUpgradeFired = useRef(false);
  useEffect(() => {
    if (autoUpgradeFired.current || !upgradeEnabled) return;
    const params = new URLSearchParams(window.location.search);
    const priceId = params.get('upgrade');
    if (!priceId) return;
    autoUpgradeFired.current = true;
    params.delete('upgrade');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    );
    if (priceId === TOPUP_KEY) {
      handleTopup();
    } else {
      handleUpgrade(priceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradeEnabled]);

  const proPlanProductId = process.env.NEXT_PUBLIC_STRIPE_PRO_PRODUCT_ID;

  // Drives the "Current plan" badge + CTA. `null` for guests (so the badge
  // doesn't sit on Free for logged-out viewers). Enterprise resolves from
  // roleName (the cached tier — a token minted before the admin grant still
  // reads Pro), so an Enterprise customer sees their card highlighted
  // rather than an Upgrade CTA.
  const currentPlan = useMemo<PlanKey | null>(() => {
    const role = session?.user?.role;
    if (!role) return null;
    if (session?.user?.roleName === 'Enterprise') return 'enterprise';
    // Admins map to Pro so the page doesn't nag them with an Upgrade CTA on
    // the Free row. They typically don't carry a real subscription.
    if (role === 'Pro' || role === 'Administrator') return 'pro';
    return 'basic';
  }, [session]);

  const stripeProduct = useMemo(() => {
    if (proPlanProductId) {
      const proProduct = stripeProducts.find((p) => p.id === proPlanProductId);
      if (proProduct) return proProduct;
    }
    return (
      stripeProducts.find((p) => p.name.includes('Pro')) || stripeProducts[0]
    );
  }, [stripeProducts, proPlanProductId]);

  const proStripePriceId =
    billingPeriod === 'yearly'
      ? stripeProduct?.prices.yearly?.id || stripeProduct?.prices.monthly?.id
      : stripeProduct?.prices.monthly?.id || stripeProduct?.prices.yearly?.id;

  const plans: PlanCard[] = useMemo(() => {
    const featuresFor = (
      key: 'free' | 'topup' | 'pro' | 'enterprise',
    ): Feature[] => {
      const raw = t(`pages.plans.${key}.features`, { returnObjects: true });
      return Array.isArray(raw) ? (raw as Feature[]) : [];
    };
    return [
      {
        key: 'basic',
        title: t('pages.plans.free.title'),
        description: t('pages.plans.free.description'),
        free: true,
        features: featuresFor('free'),
      },
      {
        key: 'topup',
        title: t('pages.plans.topup.tag'),
        description: t('pages.plans.topup.description'),
        topup: true,
        price: {
          amount: t('pages.plans.topup.rate'),
          period: t('pages.plans.topup.rateUnit'),
          vatNote: t('pages.plans.period.plusVat'),
        },
        features: featuresFor('topup'),
      },
      {
        key: 'pro',
        title: stripeProduct?.name || t('pages.plans.pro.title'),
        description:
          stripeProduct?.description || t('pages.plans.pro.description'),
        price: {
          amount: PRO_PRICE_BY_PERIOD[billingPeriod],
          period: t('pages.plans.period.perMonth'),
          subline:
            billingPeriod === 'yearly'
              ? t('pages.plans.period.billedYearly')
              : undefined,
          vatNote: t('pages.plans.period.plusVat'),
        },
        stripePriceId: proStripePriceId,
        highlighted: true,
        features: featuresFor('pro'),
      },
      {
        key: 'enterprise',
        title: t('pages.plans.enterprise.title'),
        description: t('pages.plans.enterprise.description'),
        contact: true,
        features: featuresFor('enterprise'),
      },
    ];
  }, [t, stripeProduct, proStripePriceId, billingPeriod]);

  const renderCta = (plan: PlanCard) => {
    const isCurrentPlan = currentPlan === plan.key;

    if (plan.free) {
      return (
        <button
          type="button"
          disabled
          className="w-full rounded-[10px] py-2.5 text-sm font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            background: 'transparent',
            color: 'var(--fd-ink-2)',
            border: '1px solid var(--fd-line)',
          }}
        >
          {isCurrentPlan ? t('pages.plans.ctaCurrent') : t('pages.plans.ctaFree')}
        </button>
      );
    }

    if (plan.topup) {
      if (!upgradeEnabled) {
        return (
          <button
            type="button"
            onClick={() => handleSignupToUpgrade(TOPUP_KEY)}
            className="fd-plan-cta fd-plan-cta--filled w-full cursor-pointer rounded-[10px] py-2.5 text-sm font-semibold transition-opacity"
          >
            {t('pages.plans.topup.ctaSignUp')}
          </button>
        );
      }
      return (
        <div className="flex w-full flex-col gap-3">
          {/* Pick the amount here rather than only on Stripe's page: the
              choice is "how much is this worth to me", which belongs next to
              what the credits buy, not behind a redirect. */}
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span
                className="fd-mono text-[11px] uppercase"
                style={{ color: 'var(--fd-muted)', letterSpacing: '0.08em' }}
              >
                {t('pages.plans.topup.sliderLabel', 'Amount')}
              </span>
              <span
                className="text-sm font-semibold"
                style={{ color: 'var(--fd-ink)' }}
              >
                {topupEuros} €
                <span
                  className="fd-mono ms-2 text-[11px] font-normal"
                  style={{ color: 'var(--fd-muted)' }}
                >
                  {(
                    topupEuros * CREDIT_TOPUP_CREDITS_PER_UNIT
                  ).toLocaleString('de-DE')}{' '}
                  {t('pages.plans.topup.creditsUnit', 'credits')}
                </span>
              </span>
            </div>
            {/* The thumb is a child, not a default — without one the control
                renders as a bare track with nothing to grab. */}
            <Slider
              value={[topupEuros]}
              min={TOPUP_SLIDER_MIN}
              max={TOPUP_SLIDER_MAX}
              step={1}
              onValueChange={([v]) => setTopupEuros(v)}
              aria-label={t('pages.plans.topup.sliderLabel', 'Amount')}
              // The default track is `bg-accent`, which is nearly invisible
              // against this page's warm card. Radix renders the track as the
              // first span and the filled range inside it, so both are
              // recoloured to the fd- palette and the track thickened.
              className="h-6 [&>span:first-child]:h-2 [&>span:first-child]:bg-[var(--fd-line-2)] [&>span:first-child>span]:bg-[var(--fd-accent-ink)]"
            >
              <SliderThumb
                className="size-5 border-[3px] shadow-sm"
                style={{
                  borderColor: 'var(--fd-accent-ink)',
                  background: 'var(--fd-card)',
                }}
              />
            </Slider>
            <div
              className="fd-mono flex justify-between text-[10.5px]"
              style={{ color: 'var(--fd-muted)' }}
            >
              <span>{TOPUP_SLIDER_MIN} €</span>
              <span>{TOPUP_SLIDER_MAX} €</span>
            </div>
          </div>
          <button
            type="button"
            disabled={pendingPriceId !== null}
            aria-busy={pendingPriceId === TOPUP_KEY}
            onClick={handleTopup}
            className="fd-plan-cta fd-plan-cta--filled flex w-full items-center justify-center gap-2 rounded-[10px] py-2.5 text-sm font-semibold transition-opacity disabled:opacity-60"
          >
            {pendingPriceId === TOPUP_KEY && (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            )}
            {t('pages.plans.topup.ctaAmount', {
              amount: topupEuros,
              defaultValue: 'Buy {{amount}} € of credits',
            })}
          </button>
        </div>
      );
    }

    if (plan.contact) {
      return (
        <button
          type="button"
          onClick={() => setContactOpen(true)}
          className="fd-plan-cta fd-plan-cta--outline block w-full cursor-pointer rounded-[10px] py-2.5 text-center text-sm font-semibold transition-colors"
        >
          {t('pages.plans.ctaContactSales')}
        </button>
      );
    }

    // A signup-trial user's "current plan" is Pro, but only until day 7 and
    // without a payment method — keep the upgrade button live for them.
    const trialUpgrade = plan.key === 'pro' && isTrialing;
    if (isCurrentPlan && !trialUpgrade) {
      return (
        <button
          type="button"
          disabled
          className="fd-plan-cta fd-plan-cta--filled w-full rounded-[10px] py-2.5 text-sm font-semibold disabled:opacity-60"
        >
          {t('pages.plans.ctaCurrent')}
        </button>
      );
    }

    if (!upgradeEnabled) {
      return (
        <button
          type="button"
          onClick={() => handleSignupToUpgrade(plan.stripePriceId)}
          className="fd-plan-cta fd-plan-cta--filled w-full cursor-pointer rounded-[10px] py-2.5 text-sm font-semibold transition-opacity"
        >
          {t('pages.plans.ctaSignUp')}
        </button>
      );
    }

    const canUpgrade = !!plan.stripePriceId;
    const isPending = !!plan.stripePriceId && pendingPriceId === plan.stripePriceId;
    const isBlocked = !canUpgrade || pendingPriceId !== null;
    const upgradeButton = (
      <button
        type="button"
        disabled={isBlocked}
        aria-busy={isPending}
        onClick={() => {
          if (plan.stripePriceId && !isBlocked) {
            handleUpgrade(plan.stripePriceId);
          }
        }}
        className="fd-plan-cta fd-plan-cta--filled flex w-full items-center justify-center gap-2 rounded-[10px] py-2.5 text-sm font-semibold transition-opacity disabled:opacity-60"
      >
        {isPending && (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        )}
        {t('pages.plans.ctaUpgrade')}
      </button>
    );

    if (!trialUpgrade) return upgradeButton;
    return (
      <div className="flex w-full flex-col gap-2">
        {upgradeButton}
        <p
          className="m-0 text-center text-[12px]"
          style={{ color: 'var(--fd-muted)' }}
        >
          {t('pages.plans.trialEndsNote', {
            date: trialEnd
              ? new Date(trialEnd).toLocaleDateString(i18n.language)
              : '—',
            defaultValue: 'Pro trial active — ends {{date}}.',
          })}
        </p>
      </div>
    );
  };

  return (
    <section
      style={{ background: 'var(--fd-bg)' }}
    >
      <div className="mx-auto max-w-[1320px] px-6 lg:px-8 py-20 lg:py-24">
        <div className="mb-10 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div
              className="fd-mono mb-3 text-[11px] uppercase"
              style={{
                color: 'var(--fd-muted)',
                letterSpacing: '0.12em',
              }}
            >
              {t('pages.plans.tag')}
            </div>
            <h2
              className="m-0 max-w-[820px] font-semibold"
              style={{
                fontSize: 'clamp(28px, 4vw, 42px)',
                lineHeight: 1.05,
                letterSpacing: '-0.025em',
                color: 'var(--fd-ink)',
              }}
            >
              {t('pages.plans.title')}
            </h2>
          </div>
        </div>

        <div className="mb-10 flex justify-center">
          <BillingPeriodToggle
            value={billingPeriod}
            onChange={setBillingPeriod}
          />
        </div>

        <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} />

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.key;
            return (
              <div
                key={plan.key}
                // `/plans#plan-topup` is where the top bar's "Top up credits"
                // button lands; scroll-mt clears the sticky header.
                id={`plan-${plan.key}`}
                className="relative flex scroll-mt-24 flex-col rounded-2xl border p-6"
                style={{
                  background: 'var(--fd-card)',
                  borderColor: plan.highlighted
                    ? 'var(--fd-accent-ink)'
                    : 'var(--fd-line)',
                  boxShadow: plan.highlighted
                    ? '0 0 0 1px var(--fd-accent-ink)'
                    : undefined,
                }}
              >
                {isCurrentPlan && (
                  <span
                    className="fd-mono absolute -top-2.5 start-1/2 -translate-x-1/2 rounded-full px-2.5 py-0.5 text-[10.5px] uppercase rtl:translate-x-1/2"
                    style={{
                      background: 'var(--fd-card)',
                      border: '1px solid var(--fd-good)',
                      color: 'var(--fd-good)',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {t('pages.plans.currentPlanBadge')}
                  </span>
                )}

                <div className="mb-4">
                  <div
                    className="fd-mono mb-2 text-[11px] uppercase"
                    style={{
                      color: plan.highlighted
                        ? 'var(--fd-accent-ink)'
                        : 'var(--fd-muted)',
                      letterSpacing: '0.12em',
                    }}
                  >
                    {plan.title}
                  </div>
                  <p
                    className="m-0 text-[13.5px] leading-relaxed"
                    style={{ color: 'var(--fd-ink-2)' }}
                  >
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6 min-h-[3.5rem]">
                  {plan.free ? (
                    <div
                      className="text-3xl font-semibold leading-none"
                      style={{ color: 'var(--fd-ink)' }}
                    >
                      {t('pages.plans.free.priceLabel')}
                    </div>
                  ) : plan.contact ? (
                    <div
                      className="text-2xl font-semibold leading-tight"
                      style={{ color: 'var(--fd-ink)' }}
                    >
                      {t('pages.plans.enterprise.priceLabel')}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-end gap-1.5">
                        <div
                          className="text-3xl font-semibold leading-none"
                          style={{ color: 'var(--fd-ink)' }}
                        >
                          {plan.price?.amount}
                        </div>
                        <div
                          className="fd-mono pb-0.5 text-[11.5px]"
                          style={{ color: 'var(--fd-muted)' }}
                        >
                          {plan.price?.period}
                        </div>
                      </div>
                      {plan.price?.subline && (
                        <div
                          className="fd-mono text-[11px]"
                          style={{ color: 'var(--fd-muted)' }}
                        >
                          {plan.price.subline}
                        </div>
                      )}
                      {plan.price?.vatNote && (
                        <div
                          className="fd-mono text-[11px]"
                          style={{ color: 'var(--fd-muted)' }}
                        >
                          {plan.price.vatNote}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <ul className="mb-6 flex flex-col gap-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature.text}
                      className="flex items-start gap-2 text-[14px]"
                    >
                      {feature.negative ? (
                        <X
                          className="mt-0.5 size-4 shrink-0"
                          style={{ color: 'var(--fd-muted)' }}
                          aria-hidden
                        />
                      ) : (
                        <Check
                          className="mt-0.5 size-4 shrink-0"
                          style={{ color: 'var(--fd-accent-ink)' }}
                          aria-hidden
                        />
                      )}
                      <span
                        style={{
                          color: feature.negative
                            ? 'var(--fd-muted)'
                            : 'var(--fd-ink)',
                        }}
                      >
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">{renderCta(plan)}</div>
              </div>
            );
          })}
        </div>

        <CreditUsageTable />
      </div>
    </section>
  );
};

const CreditUsageTable = () => {
  const { t } = useTranslation();

  const cellBorder = '1px solid var(--fd-line)';

  return (
    <div className="mt-16 lg:mt-20">
      <div className="mb-5">
        <div
          className="fd-mono mb-2 text-[11px] uppercase"
          style={{ color: 'var(--fd-muted)', letterSpacing: '0.12em' }}
        >
          {t('pages.plans.credits.title')}
        </div>
        <p
          className="m-0 text-[13.5px] leading-relaxed"
          style={{ color: 'var(--fd-ink-2)' }}
        >
          {t('pages.plans.credits.subtitle')}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px]">
          <LocaleLink
            href="/developer/apis"
            className="hover:underline"
            style={{ color: 'var(--fd-accent-ink)' }}
          >
            {t('pages.plans.credits.apiDocsLink')} →
          </LocaleLink>
          <LocaleLink
            href="/mcp"
            className="hover:underline"
            style={{ color: 'var(--fd-accent-ink)' }}
          >
            {t('pages.plans.credits.mcpLink')} →
          </LocaleLink>
        </div>
      </div>

      <div
        className="overflow-hidden rounded-2xl border"
        style={{ background: 'var(--fd-card)', borderColor: 'var(--fd-line)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr
                className="fd-mono text-[11px] uppercase"
                style={{
                  color: 'var(--fd-muted)',
                  letterSpacing: '0.08em',
                  borderBottom: cellBorder,
                }}
              >
                <th className="px-4 py-3 text-start font-medium">
                  {t('pages.plans.credits.colOperation')}
                </th>
                <th className="px-4 py-3 text-start font-medium">
                  {t('pages.plans.credits.colRest')}
                </th>
                <th className="px-4 py-3 text-start font-medium">
                  {t('pages.plans.credits.colMcp')}
                </th>
                <th className="px-4 py-3 text-end font-medium">
                  {t('pages.plans.credits.colCredits')}
                </th>
              </tr>
            </thead>
            <tbody>
              {CREDIT_ROWS.map((row, i) => (
                <tr
                  key={row.key}
                  style={{
                    borderTop: i === 0 ? undefined : cellBorder,
                    color: 'var(--fd-ink)',
                  }}
                >
                  <td className="px-4 py-3">
                    {t(`pages.plans.credits.rows.${row.key}`)}
                  </td>
                  <td
                    className="fd-mono px-4 py-3 text-[12px]"
                    style={{ color: 'var(--fd-ink-2)' }}
                  >
                    {row.rest}
                  </td>
                  <td
                    className="fd-mono px-4 py-3 text-[12px]"
                    style={{ color: row.mcp ? 'var(--fd-ink-2)' : 'var(--fd-muted)' }}
                  >
                    {row.mcp ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-end">
                    <span className="inline-flex flex-col items-end">
                      {row.credits === 0 ? (
                        <span style={{ color: 'var(--fd-good)' }}>
                          {t('pages.plans.credits.free')}
                        </span>
                      ) : (
                        <span className="font-semibold">{row.credits}</span>
                      )}
                      {row.realtime !== undefined && (
                        <span
                          className="fd-mono text-[10.5px]"
                          style={{ color: 'var(--fd-muted)' }}
                        >
                          {t('pages.plans.credits.realtimeNote', {
                            n: row.realtime,
                          })}
                        </span>
                      )}
                      {row.earlierVersion !== undefined && (
                        <span
                          className="fd-mono text-[10.5px]"
                          style={{ color: 'var(--fd-muted)' }}
                        >
                          {t('pages.plans.credits.earlierVersionNote', {
                            n: row.earlierVersion,
                          })}
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p
        className="mt-3 text-[12px] leading-relaxed"
        style={{ color: 'var(--fd-muted)' }}
      >
        {t('pages.plans.credits.footnote')}
      </p>

      {/* Subscription endpoint pricing */}
      <div className="mt-12">
        <div className="mb-5">
          <div
            className="fd-mono mb-2 text-[11px] uppercase"
            style={{ color: 'var(--fd-muted)', letterSpacing: '0.12em' }}
          >
            {t('pages.plans.credits.subs.title')}
          </div>
          <p
            className="m-0 text-[13.5px] leading-relaxed"
            style={{ color: 'var(--fd-ink-2)' }}
          >
            {t('pages.plans.credits.subs.lead')}
          </p>
          <p
            className="mt-2 text-[13px] leading-relaxed"
            style={{ color: 'var(--fd-muted)' }}
          >
            {t('pages.plans.credits.subs.subtitle')}
          </p>
        </div>

        <div
          className="overflow-hidden rounded-2xl border"
          style={{ background: 'var(--fd-card)', borderColor: 'var(--fd-line)' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr
                  className="fd-mono text-[11px] uppercase"
                  style={{
                    color: 'var(--fd-muted)',
                    letterSpacing: '0.08em',
                    borderBottom: cellBorder,
                  }}
                >
                  <th className="px-4 py-3 text-start font-medium">
                    {t('pages.plans.credits.subs.colType')}
                  </th>
                  <th className="px-4 py-3 text-end font-medium">
                    {t('pages.plans.credits.subs.colOneTime')}
                  </th>
                  <th className="px-4 py-3 text-end font-medium">
                    {t('pages.plans.credits.subs.colRecurring')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {SUBSCRIPTION_ROWS.map((row, i) => (
                  <tr
                    key={row.key}
                    style={{
                      borderTop: i === 0 ? undefined : cellBorder,
                      color: 'var(--fd-ink)',
                    }}
                  >
                    <td className="px-4 py-3">
                      {t(`pages.plans.credits.rows.${row.key}`)}
                    </td>
                    <td className="px-4 py-3 text-end font-semibold">
                      {row.oneTime}
                    </td>
                    <td className="px-4 py-3 text-end font-semibold">
                      {row.recurring}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p
          className="mt-3 text-[12px] leading-relaxed"
          style={{ color: 'var(--fd-muted)' }}
        >
          {t('pages.plans.credits.subs.note')}
        </p>
      </div>
    </div>
  );
};

interface BillingPeriodToggleProps {
  value: BillingPeriod;
  onChange: (value: BillingPeriod) => void;
}

const BillingPeriodToggle = ({ value, onChange }: BillingPeriodToggleProps) => {
  const { t } = useTranslation();

  const options: { key: BillingPeriod; label: string }[] = [
    { key: 'monthly', label: t('pages.plans.billingToggle.monthly') },
    { key: 'yearly', label: t('pages.plans.billingToggle.yearly') },
  ];

  return (
    <div
      role="tablist"
      aria-label="Billing period"
      className="inline-flex items-center gap-1 rounded-full p-1"
      style={{
        background: 'var(--fd-card)',
        border: '1px solid var(--fd-line)',
      }}
    >
      {options.map((option) => {
        const selected = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.key)}
            className="relative inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors"
            style={{
              background: selected ? 'var(--fd-ink)' : 'transparent',
              color: selected ? 'var(--fd-card)' : 'var(--fd-ink-2)',
            }}
          >
            {option.label}
            {option.key === 'yearly' && (
              <span
                className="fd-mono rounded-full px-1.5 py-0.5 text-[10px] uppercase"
                style={{
                  background: selected
                    ? 'var(--fd-card)'
                    : 'var(--fd-accent-tint, rgba(0,0,0,0.06))',
                  color: selected ? 'var(--fd-ink)' : 'var(--fd-accent-ink)',
                  letterSpacing: '0.08em',
                }}
              >
                {t('pages.plans.billingToggle.savingsBadge')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export { Plans };
